<template>
  <div class="template-table-of-contents">
    <div
      class="template-table-of-contents__container"
      :style="singleArticleContainerStyle"
    >
      <div class="single-article__wrapper">
        <div class="single-article">
          <section class="single-article__content">
            <AdminControlsPanel
              v-if="isAuthenticatedInWordpress"
              class="admin-controls"
              :views-count="viewsCount"
              :edit-link-href="editLinkHref"
              :loading="getLoadingViews(article.id)"
              @click-on-restore-button="updateViewsCount(article.id)"
            />
            <ArticleCard
              class="single-article-card"
              :article="article"
              :title="articleTitle"
              :comments-count="commentsCount"
              :user="user"
              :favorite="favorites"
              :folders="folders"
              :to-comments-link="false"
              :comments-enabled="article.commentsEnabled"
              @favorite-delete="(favorite) => onFavoriteDelete(favorite, article)"
              @favorite-create-folder-and-add="
                (nameFolder) => onFavoriteCreateFolderAndAdd(nameFolder, article)
              "
              @favorite-create-folder-and-transfer="
                (nameFolder, favorite) =>
                  onFavoriteCreateFolderAndTransfer(nameFolder, favorite, article)
              "
              @favorite-transfer-to-folder="
                (favorite, folder) => onFavoriteTransferToFolder(favorite, folder, article)
              "
              @favorite-add-to-folder="(folder) => onFavoriteAddToFolder(folder, article)"
              @favorite-deferred="onFavoriteDeferred"
              @clicked-on-category="onClickOnCategory"
            />

            <!-- id article указан в JSON+LD -->
            <article
              id="article"
              class="single-article__post-content single-article__content-container"
              @click="checkImageClick"
            >
              <template v-for="(content, idx) in slicedContents">
                <!-- контент статьи-->
                <TheContent
                  v-for="item of content"
                  :key="item.id"
                  :elem="item"
                  :lazy-loading-enabled="true"
                />

                <!-- виджеты внутри контента-->
                <WidgetsRendererContainer
                  v-if="
                    isWidgetsRendererVisible && typeof widgetsInsideContent[idx] !== 'undefined'
                  "
                  :widgets="widgetsInsideContent[idx]"
                />
              </template>
            </article>

            <!-- текст под контентом статьи. Обложка, источник и тп-->
            <ArticleNote
              class="article-note"
              :class="{ 'article-note_empty': !articleNotes.length }"
              :notes="articleNotes"
            />

            <!-- ArticleCardActions-->
            <component
              :is="articleCardComponent"
              class="article__actions"
              in-page-position="bottom"
              event-position="bottom"
              :article-id="article.id"
              :url="article.teaserUrl || article.url"
              :cover-url="
                article.img && article.img[0] && article.img[0].url ? article.img[0].url : null
              "
              :comments-enabled="article.commentsEnabled && !article.teaserUrl"
              :comments-count="commentsCount"
              :title="articleTitle"
              :user="user"
              :favorite="favorites"
              :folders="folders"
              :can-have-reactions="article.canHaveReactions"
              @favorite-delete="(favorite) => onFavoriteDelete(favorite, article)"
              @favorite-create-folder-and-add="
                (nameFolder) => onFavoriteCreateFolderAndAdd(nameFolder, article)
              "
              @favorite-create-folder-and-transfer="
                (nameFolder, favorite) =>
                  onFavoriteCreateFolderAndTransfer(nameFolder, favorite, article)
              "
              @favorite-transfer-to-folder="
                (favorite, folder) => onFavoriteTransferToFolder(favorite, folder, article)
              "
              @favorite-add-to-folder="(folder) => onFavoriteAddToFolder(folder, article)"
              @favorite-deferred="onFavoriteDeferred"
            />
          </section>

          <!-- зона виджетов "над комментариями"-->
          <WidgetsRendererContainer
            v-if="isWidgetsRendererVisible || isAdsException"
            class="widgets above-comments-widgets__container"
            :widgets="aboveCommentsWidgets"
          />

          <!-- комментарии-->
          <CommentsContainer
            v-if="showComments"
            :id="article.id"
            class="comments"
          />

          <!-- зона виджетов "под комментариями"-->
          <WidgetsRendererContainer
            v-if="isWidgetsRendererVisible || isAdsException"
            class="below-comments-widgets__container"
            :widgets="belowCommentsWidgets"
          />
        </div>
      </div>

      <!-- Меню-->
      <div class="table-of-contents__menu">
        <TheContent
          v-if="content.length"
          :key="content[0].id"
          :elem="content[0]"
        />
      </div>

      <!-- Полноэкранная галерея-->
      <FullScreenGallery
        v-if="isGalleryVisible"
        :image-urls="imagesSrc"
        :srcset="imagesSrcset"
        :current-slide="currentImageId"
        @close="closeGallery"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import GeneralTemplates from './general'; // Берем за основу компонент generalTemplate, но даем ему уникальное имя

  // Берем за основу компонент generalTemplate, но даем ему уникальное имя
  // Без уникального имени может рендериться не корректный шаблон
  export default defineNuxtComponent({
    ...GeneralTemplates,
    name: 'TableOfContents',
  });
</script>

<style lang="scss" scoped>
  $border: 1px solid #e7e7e7;

  .table-of-contents__menu {
    position: sticky;
    top: 72px;

    margin-right: 24px;
    display: none;
    flex-direction: column;
    flex: 0 0 300px;
    height: 100%;

    border: $border;

    background-color: #ffffff;

    @include big-tablet {
      display: flex;
      max-height: calc(100vh - 98px);
    }
  }

  .template-table-of-contents {
    &__container {
      background: #fff;
      padding: 0;
      margin: 0 auto;

      @include big-tablet {
        background: var(--bg-color-substrate);
        max-width: 1024px;
        width: 100%;
        display: flex;
        padding: 30px;
        justify-content: center;
        flex-direction: row-reverse;
      }

      @include desktop {
        max-width: 1280px;
        width: 100%;
      }
    }

    &__menu {
      @include big-tablet {
        margin-right: 22px;
      }
    }

    .single-article {
      width: 100%;
      max-width: 640px;
      margin: 0 auto;

      @include desktop {
        max-width: 782px;
        width: 782px;
      }

      &__content {
        max-width: 640px;
        background: #fff;
        margin: 0px auto 24px;

        @include tablet {
          max-width: unset;
          margin: 0 24px 24px;
        }

        @include big-tablet {
          border: 1px solid #e7e7e7;
          margin: 0 auto 24px;
        }
      }

      &__content-container {
        @include tablet {
          padding: 0;
        }

        @include big-tablet {
          padding: 0 24px;
        }
      }

      &__post-content {
        :deep(.lightgrey-box) {
          margin: 0;

          @include tablet {
            margin: 24px -24px;
          }
        }

        .table-of-contents {
          display: flex;

          @include big-tablet {
            display: none;
          }
        }
      }
    }

    .single-article-card {
      margin-bottom: 16px;
      margin: 0 auto;
      width: 100%;

      @include tablet {
        max-width: 592px;
      }

      @include desktop {
        margin-bottom: 24px;
        max-width: 782px;
      }
    }

    &.jumper-post {
      .article__footer {
        margin-bottom: 0px;
      }

      .single-article__container {
        padding-bottom: 48px;
      }
    }

    .article__footer {
      padding-top: 16px;
      margin-bottom: 16px;

      @include tablet {
        margin-bottom: 24px;
      }

      .article__actions {
        padding-bottom: 0;
      }
    }

    .comments {
      @include tablet {
        margin-bottom: 0;
      }
    }

    .admin-controls {
      background: white;
      padding-left: 16px;
      padding-top: 16px;
      width: 100%;

      @include tablet {
        padding-left: 24px;
      }
    }

    .admin-controls + .single-article-card {
      border-top: none;
      padding-top: 12px;

      @include tablet {
        padding-top: 0px;
      }
    }

    .age-calculator {
      @include tablet {
        margin: 0 0 12px;
      }
    }

    .ovulation-calculator {
      @include tablet {
        margin: 0 0 12px;
      }
    }
  }
</style>
